import type { ReactNode } from "react";
import { Modal as UiModal, type DialogDirty, type ModalSize } from "@cntrl/ui";
import styles from "./Modal.module.scss";

// The release/marketing surfaces' modal — now a thin adapter over the shared `@cntrl/ui` Modal (FE-SH-02). It
// keeps the 2-width API these 14 consumers already pass and its own `.body` padding, so their look is
// unchanged, while the backdrop/scrim/focus-management/Escape/scroll-freeze now live in the one shared,
// tested primitive. Consumed by the release editors (new-release type picker, milestone, preset, song, asset,
// smart-link) and the marketing/fans + ad-creator modals.

export interface ModalProps {
  /** Accessible dialog title, rendered in the header unless `hideHeader`. */
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** A wider variant for the 3-card type picker (shot-47). Default is the standard editor width. */
  size?: "md" | "lg";
  /** Suppress the built-in header (the type picker draws its own heading + close). */
  hideHeader?: boolean;
  /**
   * Cap the card to the viewport so the BODY scrolls under a pinned header (and whatever footer the consumer
   * pins itself), instead of the whole card growing and the backdrop scrolling it.
   *
   * Default false, which is what these 14 consumers have always had: most are short, and one grows a popover
   * that an internal scroll region would clip. Opt in only when the body has a real scroll region of its own
   * — a settings surface with a long list — and only when any in-body popup PORTALS out (`Picker` does).
   */
  fitViewport?: boolean;
  /** Unsaved edits — passed to the shared Modal's close guard (see `ModalProps.dirty`). */
  dirty?: DialogDirty;
}

// This surface's md (520) / lg (780) map onto the shared scale's md (520) / xl (780) — widths preserved exactly.
const SIZE_MAP: Record<"md" | "lg", ModalSize> = { md: "md", lg: "xl" };

export function Modal({
  title,
  onClose,
  children,
  size = "md",
  hideHeader = false,
  fitViewport = false,
  dirty,
}: ModalProps) {
  return (
    <UiModal
      title={title}
      onClose={onClose}
      size={SIZE_MAP[size]}
      hideHeader={hideHeader}
      fitViewport={fitViewport}
      dirty={dirty}
    >
      <div className={styles.body} data-fit={fitViewport || undefined}>
        {children}
      </div>
    </UiModal>
  );
}
